import { BehaviorSubject, Subject, PartialObserver } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { HubConnection } from '@microsoft/signalr';
import { nextSignalRRetryDelay } from './sg-signal-r-retry';

export interface SgSignalRConnectionHooks {
  /** Schedules fn after ms; returns a cancel function. Defaults to setTimeout. */
  schedule?: (fn: () => void, ms: number) => () => void;
  /** Called once the connection is closed for good (retries stopped, e.g. logged out). */
  onClosed?: () => void;
}

/** How long a reconnect outage may last before it is reported (once) on `errors`. */
export const SIGNALR_OUTAGE_REPORT_AFTER_MS = 60000;

const defaultSchedule = (fn: () => void, ms: number): (() => void) => {
  const handle = setTimeout(fn, ms);
  return () => clearTimeout(handle);
};

export interface SgSignalRMessage<T = any> {
  cmd: string;
  data: T;
}

export type SgSignalRConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

class SgIncomingMessageListener<T = any> {
  listener: Subject<T>;

  get connected(): boolean {
    return !!this._destroy;
  }

  private _destroy: Subject<void>;

  constructor(public readonly name: string) {
    this.listener = new Subject<T>();
  }

  subscribe(handler: PartialObserver<SgSignalRMessage<T>>): void {
    this.disconnect();

    this._destroy = new Subject<void>();
    this.listener
      .pipe(
        map((data) => ({ cmd: this.name, data })),
        takeUntil(this._destroy)
      )
      .subscribe(handler);
  }

  disconnect(): void {
    if (this.connected) {
      this._destroy.next();
      this._destroy.complete();
      this._destroy = undefined;
    }
  }
}

export class SgSignalRConnection<T = any> {
  connected = new BehaviorSubject<boolean>(false);

  readonly errors = new Subject<string | Error>();
  readonly incoming = new Subject<SgSignalRMessage<T>>();

  private conn: HubConnection;
  private listeners = new Map<string, SgIncomingMessageListener>();
  private _destroy = new Subject<void>();

  private _disposed = false;
  private _closed = false;
  private _cancelRetry?: () => void;
  private _cancelOutageWatch?: () => void;
  private _lastRetryReason?: unknown;

  constructor(
    conn: HubConnection,
    private hooks: SgSignalRConnectionHooks = {}
  ) {
    this.conn = conn;
    conn.onclose((error) => {
      this.cancelOutageWatch();
      if (error && !this._disposed) {
        this.errors.next(error);
      }
      // Final close: tear listeners down even if onreconnecting already reported connected=false
      // (in which case disconnect() alone would be a no-op).
      this.disconnect();
      this.onDisconnect();
      this.notifyClosed();
    });
    // Reconnect retries indefinitely, so report the outage instead of staying "connected" throughout it.
    // Hub listeners (conn.on) survive a reconnect, so nothing is torn down here.
    // SignalR 6 fires onreconnecting once per outage, after the retry policy's first call (which already noted a
    // reason), so only record the close error if there is one.
    conn.onreconnecting((error) => {
      this.connected.next(false);
      this.noteRetryReason(error);
      this.watchOutage();
    });
    conn.onreconnected(() => {
      this.cancelOutageWatch();
      this.connected.next(true);
    });

    this.start(0);
  }

  /** Records why the latest reconnect attempt failed (fed by SgSignalR's retry policy). */
  noteRetryReason(reason: unknown): void {
    if (reason !== undefined) {
      this._lastRetryReason = reason;
    }
  }

  /**
   * SignalR's reconnect loop logs nothing, and with endless retries onclose only fires on logout, so an outage
   * that outlasts SIGNALR_OUTAGE_REPORT_AFTER_MS is reported once, with the latest retry reason. Short blips stay silent.
   */
  private watchOutage(): void {
    this.cancelOutageWatch();
    const schedule = this.hooks.schedule ?? defaultSchedule;
    this._cancelOutageWatch = schedule(() => {
      this._cancelOutageWatch = undefined;
      if (!this._disposed && !this._closed) {
        const reason = this._lastRetryReason;
        const detail = reason instanceof Error ? reason.message : reason ? String(reason) : 'connection lost';
        this.errors.next(
          new Error(`Live notifications still reconnecting after ${SIGNALR_OUTAGE_REPORT_AFTER_MS / 1000}s: ${detail}`)
        );
      }
    }, SIGNALR_OUTAGE_REPORT_AFTER_MS);
  }

  private cancelOutageWatch(): void {
    this._cancelOutageWatch?.();
    this._cancelOutageWatch = undefined;
  }

  private notifyClosed(): void {
    if (!this._closed) {
      this._closed = true;
      this.hooks.onClosed?.();
    }
  }

  /**
   * The first start() isn't covered by withAutomaticReconnect (that only kicks in after a connection was
   * established), so retry it with the same policy instead of staying disconnected until a page reload.
   */
  private start(previousRetryCount: number): void {
    this.conn
      .start()
      .then(() => {
        if (!this._disposed) {
          this.connected.next(true);
        }
      })
      .catch((error) => {
        if (this._disposed) {
          this.notifyClosed();
          return;
        }
        const delay = nextSignalRRetryDelay(previousRetryCount, error);
        // Report the first failure of an outage and the one that ends retrying; the others would repeat every minute.
        if (previousRetryCount === 0 || delay === null) {
          this.errors.next(error);
        }
        if (delay === null) {
          this.notifyClosed();
          return;
        }
        const schedule = this.hooks.schedule ?? defaultSchedule;
        this._cancelRetry = schedule(() => {
          this._cancelRetry = undefined;
          if (!this._disposed) {
            this.start(previousRetryCount + 1);
          }
        }, delay);
      });
  }

  setListener(name: string): void {
    if (!this.listeners.has(name)) {
      const listener = new SgIncomingMessageListener(name);
      this.conn.on(name, (data: any) => {
        listener.listener.next(data);
      });
      listener.subscribe(this.incoming);
      this.listeners.set(name, listener);
    }
  }

  removeListener(name: string): void {
    if (this.listeners.has(name)) {
      const l = this.listeners.get(name);
      if (l) {
        l.disconnect();
      }
    }
  }

  disconnect(): void {
    if (this.connected.value) {
      this.connected.next(false);
      this.onDisconnect();
    }
  }

  dispose(): void {
    this._disposed = true;
    this._cancelRetry?.();
    this._cancelRetry = undefined;
    this.cancelOutageWatch();
    this.disconnect();
    this.conn.stop();
    this._destroy.next();
    this.connected.complete();
    this.errors.complete();
    this.incoming.complete();
    this._destroy.complete();
  }

  private onDisconnect(): void {
    for (let [name, listener] of Array.from(this.listeners.entries())) {
      if (listener) {
        listener.disconnect();
      }
    }
  }
}
